import { mapActions, mapGetters } from 'vuex'
import StripeConnectMixin from '~/components/stripeConnectMixin.js'

export default {
	mixins: [StripeConnectMixin],
	data: () => ({
		FORMATTED_INTEGRATION_NAMES: {
			'google-calendar': 'Google Calendar',
			zoom: 'Zoom',
			'stripe-connect': 'Stripe',
			paypal: 'PayPal',
		},
	}),
	async created() {
		await this.fetchStore()
	},
	computed: {
		...mapGetters('SiteSettingsStore', [
			'getIntegrations',
			'getStripeConnectedAccountStatus',
			'isPaypalConnected',
			'isPaypalChargesEnabled',
			'isPaypalEmailConfirmed',
			'getStanPaymentStatus',
		]),
		...mapGetters('Stores', ['getStore']),
		hasProductWithErroredIntegration() {
			if (!this.getStore?.pages) {
				return false
			}

			for (const page of this.getStore?.pages) {
				if (this.checkIfProductHasErroredIntegration(page.data.product)) {
					return true
				}
			}
			return false
		},
		hasErroredIntegrations() {
			return this.erroredIntegrations.length > 0
		},
		erroredIntegrations() {
			return Object.values(this.getIntegrations)
				.filter(value => value.status === this.$constants.INTEGRATION_STATUS.ERRORED)
				.map(value => value.type)
		},
		formattedStringOfErroredIntegrations() {
			const formattedNames = this.erroredIntegrations.map(integration => this.FORMATTED_INTEGRATION_NAMES[integration])
			if (formattedNames.length === 1) {
				return formattedNames[0]
			}
			const last = formattedNames.pop()
			return `${formattedNames.join(', ')} and ${last}`
		},
		missingIntegrationErrorBannerMessage() {
			return `Please go to the Integrations tab to reconnect ${this.formattedStringOfErroredIntegrations}!`
		},
		hasStripeAccountIssues() {
			return [
				this.$constants.STRIPE_ACCOUNT_STATUS.REJECTED,
				this.$constants.STRIPE_ACCOUNT_STATUS.RESTRICTED,
				this.$constants.STRIPE_ACCOUNT_STATUS.RESTRICTED_SOON,
			].includes(this.getStripeConnectedAccountStatus?.status)
		},
		stripeAccountErrorBannerMessage() {
			return this.getStripeConnectedAccountStatus?.status_details
		},
		hasStanPaymentIssues() {
			return this.getStanPaymentStatus?.status === this.$constants.STRIPE_ACCOUNT_STATUS.INACTIVE
		},
		stanPaymentErrorBannerMessage() {
			return this.getStanPaymentStatus?.status_details
		},
		hasPaypalAccountIssues() {
			return this.isPaypalConnected && !this.isPaypalChargesEnabled
		},
		paypalAccountErrorBannerMessage() {
			if (this.hasPaypalAccountIssues && this.isPaypalEmailConfirmed) {
				return 'Your account connection is not complete.'
			}
			if (this.hasPaypalAccountIssues) {
				return 'To accept payments, please verify your PayPal email address.'
			}
			return 'other test'
		},
		paypalButton() {
			if (this.hasPaypalAccountIssues && this.isPaypalEmailConfirmed) {
				return {
					name: 'Learn More',
					click: () => window.open('https://help.stan.store/article/147-why-is-paypal-not-connecting-to-my-stan-store', '_blank'),
				}
			}
			return { name: 'Update Now', click: () => this.managePaypalAccount() }
		},
	},
	methods: {
		...mapActions('Stores', ['fetchStore']),
		...mapActions('SiteSettingsStore', ['fetchStripeConnected']),
		isErroredIntegration(integrationType) {
			return this.erroredIntegrations.includes(integrationType)
		},
		checkIfProductHasErroredIntegration(product) {
			switch (product?.type) {
				case 'meeting':
					return this.checkIfMeetingProductHasErroredIntegration(product)
				default:
					return false
			}
		},
		checkIfMeetingProductHasErroredIntegration(product) {
			return this.erroredIntegrations.includes(product.meeting.platform)
		},
		navigateToPayments() {
			this.$router.push({ name: 'account_settings', query: { tab: 'store' } })
		},
		manageStripeAccount() {
			this.loading = true
			this.$axios
				.post('v1/integrations/stripe/connect', {})
				.then(response => {
					const authPopupWindow = this.$commonHelper.createCenteredPopupWin(response.data.url, 'Connect Stripe Integration', 500, 800)
					this.loading = false
					if (authPopupWindow) {
						const timer = setInterval(() => {
							if (authPopupWindow.closed) {
								this.fetchStripeConnected()
								clearInterval(timer)
							}
						}, 300)
					}
				})
				.catch(error => {
					this.$stanNotify({
						type: 'error',
						title: this.$t('Something went wrong'),
						text: error?.response?.data?.message ?? this.$t('Please try again later.'),
					})
					this.$logError(error)
					this.loading = false
				})
		},
		manageStanPaymentAccount() {
			this.connectStanPayment()
		},
		managePaypalAccount() {
			const authPopupWindow = this.$commonHelper.createCenteredPopupWin('https://paypal.com', 'Connect Paypal Integration', 500, 800)
			if (!authPopupWindow) {
				// eslint-disable-next-line no-alert
				alert(this.$t('Please allow popups for this website'))
			}
		},
	},
}
